import { useEffect, useState, useRef, memo } from "react";
import { FormHeader, RadioGroupField } from "@/components/ui";
import {
	agri_loan_types,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_LIVESTOCK,
	LOAN_TYPE_AGRI_PROPERTY,
	LOAN_TYPE_HOME,
	LOAN_TYPE_OTHER,
} from "@/lib/constants";
import {
	TractorIcon,
	SuitcaseIcon,
	CowIcon,
	BarnIcon,
	HouseLineIcon,
	DotsThreeOutlineIcon,
} from "@phosphor-icons/react";

export interface FinanceFormData {
	loanType: string;
}

export interface FinanceFormErrors {
	loanType: boolean;
}

interface FormDataProps {
	formData: FinanceFormData;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	formErrors: FinanceFormErrors;
	setFormErrors: React.Dispatch<React.SetStateAction<any>>;
}

// Constants
const RESPONSIVE_BREAKPOINT = 400;

function FinanceForm({ formData, setFormData, formErrors, setFormErrors }: FormDataProps) {
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [formContainer, setFormContainer] = useState(0);

	const headingRef = useRef<HTMLHeadingElement | null>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null);

	// Handle responsive layout with ResizeObserver
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			setIsNavDivSmall(entry.contentRect.width < RESPONSIVE_BREAKPOINT);
		});

		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	// For form container
	useEffect(() => {
		const limiter = 600;

		setIsNavDivSmall(formContainer < limiter);
	}, [formContainer]);

	return (
		<div ref={navDivRef} className="jF-flex jF-flex-col jF-space-y-4">
			<FormHeader
				icon="tag"
				title="Select Finance Option"
				description="Select the finance option you're interested in."
				headingRef={headingRef}
			/>

			{/* Loan Types */}
			<div>
				<RadioGroupField
					id="loanType"
					label="Finance Options"
					value={formData.loanType}
					onChange={(value) => {
						setFormData((prevData: any) => ({ ...prevData, loanType: value }));
						setFormErrors((prevErrors: any) => ({ ...prevErrors, loanType: false }));
					}}
					options={agri_loan_types.map((item) => ({
						id: item.option,
						value: item.option,
						label: item.option,
						icon:
							item.value == LOAN_TYPE_EQUIPMENT ? (
								<TractorIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_LIVESTOCK ? (
								<CowIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_AGRI_PROPERTY ? (
								<BarnIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_HOME ? (
								<HouseLineIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_OTHER ? (
								<DotsThreeOutlineIcon size={24} weight="duotone" />
							) : null,
					}))}
					error={formErrors.loanType ? "This field is required" : ""}
					required={false}
					disabled={false}
					className={""}
					gridCols={3}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			</div>
			{formErrors.loanType && (
				<span className="jF-text-red-700 jF-text-xs">This field is required</span>
			)}
		</div>
	);
}

export default memo(FinanceForm);
